import React from 'react';
import {Badge, Button, Spinner} from "react-bootstrap";
import moment from 'moment';
import {PartnerCredential, PartnerCredentialStatus} from '../../Partners/models';

const EXPIRY_WARNING_DAYS = 30;

interface PartnerCredentialsTableProps {
    credentials: PartnerCredential[];
    revokingCredentialId: number | null;
    togglingCredentialId: number | null;
    disabled?: boolean;
    onRevoke: (credentialId: number) => void;
    onToggleActive: (credentialId: number, isActive: boolean) => void;
}

export function PartnerCredentialsTable(props: PartnerCredentialsTableProps) {
    return (
        <div className="row m-0 mt-3">
            <table className='generic-table table table-responsive-md table-hover'>
                <thead>
                <tr>
                    <th>Nimi</th>
                    <th>Staatus</th>
                    <th>Aegub</th>
                    <th>Viimati kasutatud</th>
                    <th>Tegevused</th>
                </tr>
                </thead>
                <tbody>
                { props.credentials.map((c) => {
                    const display = getStatusDisplay(c);
                    const revokable = isRevokable(c.status);
                    const toggling = props.togglingCredentialId === c.id;
                    const revoking = props.revokingCredentialId === c.id;

                    return (
                        <tr key={'credential-' + c.id}>
                            <td className="text-nowrap">{c.name ?? '-'}</td>
                            <td className="text-nowrap">
                                <Badge 
                                    variant={display.variant}
                                    style={{width: 90}}
                                >
                                    {display.label}
                                </Badge>
                            </td>
                            <td className="text-nowrap">
                                {c.expiresAt ? moment(c.expiresAt).format('DD.MM.YYYY') : '-'}
                            </td>
                            <td className="text-nowrap">
                                {c.lastUsedAt ? moment(c.lastUsedAt).format('DD.MM.YYYY HH:mm') : '-'}
                            </td>
                                <td>
                                    {
                                        revokable && (
                                            <div className="d-flex">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    disabled={props.disabled || toggling}
                                                    onClick={() => props.onToggleActive(c.id, c.status !== PartnerCredentialStatus.ACTIVE)}
                                                    className="d-flex align-items-center justify-content-center m-auto"
                                                >
                                                    { toggling ? <Spinner size="sm" animation="border"/> : <span className='px-2'>{c.status === PartnerCredentialStatus.ACTIVE ? 'Peata' : 'Taasta'}</span> }
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    className="d-flex align-items-center justify-content-center m-auto"
                                                    disabled={props.disabled || revoking}
                                                    onClick={() => props.onRevoke(c.id)}
                                                >
                                                    { revoking ? <Spinner size="sm" animation="border"/> : <span className='px-2'>Tühista</span> }
                                                </Button>
                                            </div>
                                        )
                                    }
                            </td>
                        </tr>
                    )
                })}
                </tbody>
            </table>
        </div>
    );
}

const isRevokable = (status: PartnerCredentialStatus) =>
    status === PartnerCredentialStatus.ACTIVE || status === PartnerCredentialStatus.INACTIVE;

type StatusDisplay = {
    variant: 'primary' | 'warning' | 'danger' | 'secondary' | 'dark';
    label: string;
};

const getStatusDisplay = (credential: PartnerCredential): StatusDisplay => {
    if (credential.status === PartnerCredentialStatus.REVOKED) {
        return {variant: 'danger', label: 'Tühistatud'};
    }
    if (credential.status === PartnerCredentialStatus.EXPIRED) {
        return {variant: 'secondary', label: 'Aegunud'};
    }
    if (credential.status === PartnerCredentialStatus.INACTIVE) {
        return {variant: 'dark', label: 'Peatatud'};
    }
    if (credential.expiresAt && moment(credential.expiresAt).diff(moment(), 'days') <= EXPIRY_WARNING_DAYS) {
        return {variant: 'warning', label: 'Aeguv'};
    }
    return {variant: 'primary', label: 'Kehtiv'};
};